import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { isEmpty } from 'underscore';
import { message } from 'antd';
import Api from '../api/Api';
import { QUERY_KEYS } from './queryKeys';

export const useAvailableDatasetsQuery = (
  coordinates: number[],
  showWarning = false
) => {
  const queryClient = useQueryClient();

  const fetchAvailableDatasets = async (
    coords: number[],
    shouldShowWarning = false
  ) => {
    if (!coords || coords.length !== 2) {
      return null;
    }
    try {
      const datasetRep = await Api.detail.getBestDatasets(
        coords,
        shouldShowWarning
      );

      if (!datasetRep) {
        if (shouldShowWarning) {
          message.warn('Waves, Ocean and Wind dataset is not found!');
        }
        return null;
      }

      const missingDataset = [];
      if (isEmpty(datasetRep.SW) || datasetRep.SW.length < 1) {
        missingDataset.push('Waves');
      }
      if (isEmpty(datasetRep.HD) || datasetRep.HD.length < 1) {
        missingDataset.push('Ocean');
      }
      if (isEmpty(datasetRep.Wind) || datasetRep.Wind.length < 1) {
        missingDataset.push('Wind');
      }

      if (shouldShowWarning && missingDataset.length > 0) {
        const errorMessage =
          missingDataset.length === 1
            ? missingDataset[0]
            : missingDataset.length === 2
            ? missingDataset[0] + ' and ' + missingDataset[1]
            : missingDataset[0] +
              ', ' +
              missingDataset[1] +
              ' and ' +
              missingDataset[2];
        message.warn(errorMessage + ' dataset is not found!');
      }

      if (missingDataset.length === 3) {
        return null;
      }

      return datasetRep;
    } catch (error) {
      console.error('Error fetching available datasets:', error);
      if (shouldShowWarning) {
        message.error('Failed to fetch dataset information');
      }
      return null;
    }
  };

  const queryKey = [QUERY_KEYS.dataset.bestDatasets, coordinates.join(',')];
  const { data } = useQuery({
    queryKey,
    queryFn: () => fetchAvailableDatasets(coordinates, showWarning),
    enabled: !!coordinates && coordinates.length === 2,
  });

  const changeCoordinatesMutation = useMutation({
    mutationKey: [QUERY_KEYS.dataset.bestDatasets],
    mutationFn: async ({
      newCoords,
      shouldShowWarning,
    }: {
      newCoords: number[];
      shouldShowWarning?: boolean;
    }) => {
      return await fetchAvailableDatasets(
        newCoords,
        shouldShowWarning ?? showWarning
      );
    },
    onSuccess: (result, variables) => {
      if (result) {
        queryClient.setQueryData(
          [QUERY_KEYS.dataset.bestDatasets, variables.newCoords.join(',')],
          result
        );
      }
    },
  });

  return {
    bestDatasets: data,
    changeCoordinates: (newCoords: number[], shouldShowWarning?: boolean) =>
      changeCoordinatesMutation.mutateAsync({ newCoords, shouldShowWarning }),
  };
};
